import { dramas } from '@/mocks/dramas';
import { actors } from '@/mocks/actors';
import { news } from '@/mocks/news';
import { genres } from '@/mocks/genres';
import type { Drama, Actor, NewsItem, Genre } from '@/types';

export const getDramaById = (id?: string): Drama | undefined =>
  dramas.find((d) => d.id === id);

export const getActorById = (id?: string): Actor | undefined =>
  actors.find((a) => a.id === id);

export const getActorByName = (name?: string): Actor | undefined =>
  actors.find((a) => a.name === name);

export const getGenreByKey = (key?: string): Genre | undefined =>
  genres.find((g) => g.key === key);

export const getGenreByName = (name?: string): Genre | undefined =>
  genres.find((g) => g.name === name);

export const getDramasByGenre = (genreName?: string): Drama[] =>
  genreName ? dramas.filter((d) => d.genres.includes(genreName)) : dramas;

export const getDramasByActor = (actorId: string): Drama[] =>
  dramas.filter((d) => d.cast.includes(actorId));

export const getRelatedDramas = (drama: Drama, limit = 4): Drama[] => {
  const scored = dramas
    .filter((d) => d.id !== drama.id)
    .map((d) => {
      const genreScore = d.genres.filter((g) => drama.genres.includes(g)).length * 2;
      const castScore = d.cast.filter((c) => drama.cast.includes(c)).length * 3;
      return { d, score: genreScore + castScore };
    })
    .filter((item) => item.score > 0)
    .sort((a, b) => b.score - a.score);
  return scored.slice(0, limit).map((item) => item.d);
};

export const getNewestDramas = (limit = 8): Drama[] =>
  [...dramas].sort((a, b) => b.year - a.year || b.heat - a.heat).slice(0, limit);

export const getHotNews = (limit = 5): NewsItem[] => news.slice(0, limit);

export interface SearchResult {
  dramas: Drama[];
  actors: Actor[];
  news: NewsItem[];
}

export const searchData = (keyword: string): SearchResult => {
  const kw = keyword.trim().toLowerCase();
  if (!kw) return { dramas: [], actors: [], news: [] };
  return {
    dramas: dramas.filter(
      (d) =>
        d.title.toLowerCase().includes(kw) ||
        d.titleEn.toLowerCase().includes(kw) ||
        d.genres.some((g) => g.includes(kw)) ||
        d.cast.some((c) => {
          const actor = getActorById(c);
          return actor ? actor.name.toLowerCase().includes(kw) || actor.nameEn.toLowerCase().includes(kw) : false;
        }) ||
        d.director.includes(kw),
    ),
    actors: actors.filter(
      (a) => a.name.toLowerCase().includes(kw) || a.nameEn.toLowerCase().includes(kw),
    ),
    news: news.filter(
      (n) =>
        n.title.toLowerCase().includes(kw) ||
        n.summary.toLowerCase().includes(kw) ||
        n.category.includes(kw),
    ),
  };
};